import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  clearElementsForLocalStorage,
  getNonDeletedElements,
  hashElementsVersion,
} from "../../packages/excalidraw/element";
import { newElementWith } from "../../packages/excalidraw/element/mutateElement";
import { clearAppStateForLocalStorage } from "../../packages/excalidraw/appState";
import { EVENT } from "../../packages/excalidraw/constants";
import { ExcalidrawElement } from "../../packages/excalidraw/element/types";
import {
  AppState,
  BinaryFiles,
  ExcalidrawImperativeAPI,
} from "../../packages/excalidraw/types";
import { LocalData } from "../data/LocalData";
import { cloneJSON } from "../../packages/excalidraw/utils";
import {
  saveSceneToCloud,
  CLOUD_FILE_INITIAL_VERSION,
} from "../data/cloudScene";
import { fireNudgeTrigger } from "../components/canvas/feedback/feedbackNudgeLogic";
import { createFile, getFile } from "../services/filesService";
import {
  createAutosaveScheduler,
  AutosaveScheduler,
} from "../data/autosaveScheduler";
import {
  fileLifecycleReducer,
  FileLifecycleState,
  initialLifecycleState,
} from "../data/fileLifecycle";
import { isVersionConflict } from "../data/versionConflict";
import {
  saveCloudFileBuffer,
  clearCloudFileBuffer,
  loadCloudFileBuffer,
} from "../data/cloudFileBuffer";
import {
  classifySaveFailure,
  isBrowserOnline,
  SaveFailureKind,
} from "../data/saveFailure";
import { isSafeToSaveCloudScene } from "../data/cloudSaveGate";
import { createThumbnailTrigger } from "../data/thumbnailTrigger";
import { generateAndUploadThumbnail, deleteThumbnail } from "../data/thumbnail";

/**
 * useCloudAutosave — wires the tested pure pieces (scheduler, lifecycle
 * reducer, version-conflict check, per-file buffer) into the live editor.
 *
 * Responsibilities kept here (integration glue only; the decisions are pure and
 * unit-tested elsewhere):
 *  - route cloud-file edits to a debounced, single-flight cloud save;
 *  - create-on-first-edit for a "New mindmap" (no DB row until real content);
 *  - keep failed/offline saves in a per-file localStorage buffer and retry with
 *    backoff, flushing on reconnect;
 *  - expose the status-chip state and the multi-tab conflict flag.
 *
 * The scratch canvas is never touched here: cloud edits go to the per-file
 * buffer + cloud, exclusively.
 */

/**
 * `offline` = we could not reach a server. `error`/`failed` = the server
 * answered with an error (see data/saveFailure.ts — an answered request must
 * never be reported as "offline").
 */
export type SaveStatus =
  | "idle"
  | "saving"
  | "saved"
  | "offline"
  | "error"
  | "failed";

const INITIAL_BACKOFF_MS = 2000;
const MAX_BACKOFF_MS = 60000;

/**
 * How often a visible tab asks the server whether another writer (an agent,
 * another tab) committed a newer version — there is no realtime channel, so
 * this is how an idle tab learns of it. 30s is fine: the probe is one
 * primary-key read, and returning to the tab probes immediately on focus.
 */
export const REMOTE_VERSION_PROBE_INTERVAL_MS = 30_000;

const REMOTE_UPDATE_TOAST = "Updated elsewhere";

const hasContent = (elements: readonly ExcalidrawElement[]) =>
  elements.some((element) => !element.isDeleted);

/**
 * Content signature used to tell a real edit from an echo. Programmatic
 * `updateScene` (loading a file) fires `onChange`, so without this every file
 * open would trigger a spurious save + version bump. Based on element versions
 * plus the background color (ignores volatile pan/zoom to avoid churn).
 */
const sceneSignature = (
  elements: readonly ExcalidrawElement[],
  appState: Pick<AppState, "viewBackgroundColor">,
): string => `${hashElementsVersion(elements)}:${appState.viewBackgroundColor}`;

/** Mark on-canvas image elements as saved once their blobs are persisted. */
const markImagesSaved = (excalidrawAPI: ExcalidrawImperativeAPI) => {
  let didChange = false;
  const elements = excalidrawAPI
    .getSceneElementsIncludingDeleted()
    .map((element) => {
      if (LocalData.fileStorage.shouldUpdateImageElementStatus(element)) {
        const next = newElementWith(element, { status: "saved" });
        if (next !== element) {
          didChange = true;
        }
        return next;
      }
      return element;
    });
  if (didChange) {
    excalidrawAPI.updateScene({ elements });
  }
};

export interface UseCloudAutosaveOptions {
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  /** Current access token (may resolve after mount). */
  accessToken: string | undefined;
  /** File id from the `/f/<id>` path, or null on `/`. */
  fileIdFromPath: string | null;
  /** Server version of the loaded file, for conflict detection baseline. */
  loadedVersion?: number | null;
  /** Called with the new id after create-on-first-edit; parent navigates. */
  onCreated: (fileId: string) => void;
  /**
   * Live-collab probe. During a session an image's `status: "saved"` is the
   * protocol signal "this blob is in the room bucket", so a local IndexedDB write
   * must not stamp it (an invitee joining before the room upload lands 404s).
   */
  isCollaborating?: () => boolean;
  /**
   * Re-load this file's scene from the cloud, discarding what is on screen.
   * `silent`: the tab was clean (an agent committed meanwhile) — no loading
   * overlay, and apply without a history entry.
   */
  onReloadFromCloud?: (options?: { silent?: boolean }) => void;
}

/**
 * A save the server refused because another writer committed first. `current`
 * is the version that won, and the version any recovery must build on.
 */
export interface CloudSaveConflict {
  current: number;
}

export interface UseCloudAutosaveResult {
  /** True whenever a cloud file (open, pending-new, or creating) is active. */
  isCloudContext: boolean;
  /**
   * A cloud context with NO row behind it yet (`pendingNew`): the first edit is
   * what creates the file. Nothing cloud-side can hold this scene — the offline
   * buffer is keyed by fileId — so the caller must keep persisting it locally.
   */
  isPreCreate: boolean;
  status: SaveStatus;
  /** epoch ms of the last successful save (for the relative-time chip). */
  savedAt: number | null;
  /** Set once a save reveals another tab wrote the file. */
  hasConflict: boolean;
  /**
   * Non-null while a save has been refused as stale and the user has not yet
   * chosen what to do. Autosave is paused meanwhile — a retry could only be
   * refused again.
   */
  conflict: CloudSaveConflict | null;
  /**
   * Answer the conflict. `"reload"` throws the tab's work away and re-loads the
   * file; `"keep"` commits the tab's content on top of the version that won.
   */
  resolveConflict: (choice: "reload" | "keep") => void;
  /**
   * Set when the last save SUCCEEDED but permanently skipped one or more images
   * (too large). Not a failure — actionable advice — and cleared by the next
   * clean save.
   */
  imageWarning: string | null;
  /**
   * The reason the last save failed, for a status that would otherwise only say
   * "Save failed". `null` while things are fine.
   */
  errorMessage: string | null;
  /**
   * True when the open cloud file holds edits that are NOT in the cloud yet
   * (debounce still pending, a save in flight, or the last save failed). Drives
   * the "you have unsaved work" confirmation before destructive actions.
   */
  hasUnsavedChanges: boolean;
  /** Route an editor change through the cloud save pipeline. */
  handleChange: (
    elements: readonly ExcalidrawElement[],
    appState: Pick<AppState, "viewBackgroundColor">,
  ) => void;
  /**
   * Declare the given scene as the in-sync-with-cloud baseline (called right
   * after hydrating from the cloud) so the resulting `onChange` echo doesn't
   * trigger a redundant save.
   *
   * `version`: the server version that scene was read at. Pass it whenever the
   * loader has it — a reload can return a newer version than the one that
   * triggered it, and the next save must build on that number.
   */
  markSynced: (
    elements: readonly ExcalidrawElement[],
    appState: Pick<AppState, "viewBackgroundColor">,
    version?: number | null,
  ) => void;
  /**
   * Declare that the open file's scene (from the cloud, or from its offline
   * buffer) is now in the editor. Until this is called, edits to an `open` file
   * are NOT saved — the editor is still showing the previous scene, and writing
   * it would destroy the file (see data/cloudSaveGate). Must be called on every
   * successful hydration path, including a restored buffer (which is
   * deliberately not `markSynced`, so that it still flushes).
   */
  markHydrated: () => void;
  /**
   * Enter "New mindmap": fresh editor, no DB row until the first real edit.
   *
   * By default any pending edit to the outgoing file is flushed to the cloud
   * first (never lose work silently). `discardPending` is the user explicitly
   * choosing to throw those edits away: the pending save is cancelled AND the
   * file's offline buffer is dropped, so the file stays at its last saved
   * version rather than resurrecting the discarded scene on next open.
   */
  startNewMindmap: (options?: { discardPending?: boolean }) => void;
  /** Flush any pending save immediately (e.g. before unload). */
  flushNow: () => void;
  /**
   * Save right now and resolve with whether it landed. Unlike `flushNow` this
   * can be awaited, which is what "Save it first" needs before it resets the
   * canvas: in `pendingNew` the save IS the file creation, and the un-awaited
   * version let the reset wipe the scene (and its scratch copy) mid-create.
   */
  saveNow: () => Promise<boolean>;
  /**
   * beforeunload path: synchronously buffer the newest scene (even mid-save,
   * which `flushNow` alone skips) and return whether the open cloud file still
   * has unsaved changes (to drive the browser's unload confirm).
   */
  flushBeforeUnload: () => boolean;
}

export const useCloudAutosave = ({
  excalidrawAPI,
  accessToken,
  fileIdFromPath,
  loadedVersion,
  onCreated,
  isCollaborating,
  onReloadFromCloud,
}: UseCloudAutosaveOptions): UseCloudAutosaveResult => {
  const [lifecycle, setLifecycle] = useState<FileLifecycleState>(() =>
    fileIdFromPath
      ? { kind: "open", fileId: fileIdFromPath }
      : initialLifecycleState,
  );
  const [status, setStatus] = useState<SaveStatus>("idle");
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [hasConflict, setHasConflict] = useState(false);
  const [conflict, setConflict] = useState<CloudSaveConflict | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Permanently-skipped-image note from the last successful save, and the real
  // reason behind the last failure. Both are cleared by the next clean save:
  // stale advice about a problem that no longer exists is its own bug.
  const [imageWarning, setImageWarning] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Refs mirror state for use inside async callbacks / the scheduler, which are
  // created once and must not close over stale renders.
  const lifecycleRef = useRef(lifecycle);
  const apiRef = useRef(excalidrawAPI);
  // The editor clears itself before passive cleanup. Retain detached save data
  // while it is alive, so teardown never interprets that reset as a user edit.
  const latestSceneRef = useRef<{
    fileId: string;
    elements: readonly ExcalidrawElement[];
    appState: AppState;
    files: BinaryFiles;
  } | null>(null);
  const mountedRef = useRef(true);
  const tokenRef = useRef(accessToken);
  const lastVersionRef = useRef<number | null>(loadedVersion ?? null);
  const onCreatedRef = useRef(onCreated);
  const isCollaboratingRef = useRef(isCollaborating);
  const onReloadFromCloudRef = useRef(onReloadFromCloud);
  // Mirrors `conflict` for the scheduler's async callbacks: while it is set,
  // every save path is a no-op until the user answers the banner.
  const conflictRef = useRef<CloudSaveConflict | null>(null);
  const backoffRef = useRef(INITIAL_BACKOFF_MS);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Consecutive failed saves, to escalate "couldn't save — retrying" into a
  // hard "save failed" once retrying is evidently not helping.
  const failureCountRef = useRef(0);
  const onFlushRef = useRef<() => Promise<void>>();
  // Signature of the scene currently believed to match the cloud (or null until
  // a baseline is established). Guards against saving hydration echoes.
  const syncedSignatureRef = useRef<string | null>(null);
  // Has the open file's scene actually been loaded into the editor? Until it
  // has, the editor is still showing the PREVIOUS (scratch) scene and must not
  // write it to the file — see data/cloudSaveGate.
  const hydratedRef = useRef(!fileIdFromPath);
  // Bumped when a teardown buffers a scene; an in-flight save only clears the
  // buffer if it hasn't advanced (see bufferCurrentSceneForTeardown).
  const bufferGenerationRef = useRef(0);
  // Signature of the scene the in-flight save captured, or null when no save is
  // in flight. Lets a teardown tell "a newer edit landed mid-save" (must be
  // buffered) from "same scene, nothing new" (must NOT claim the buffer, or the
  // save can never clear it — see bufferCurrentSceneForTeardown).
  const inFlightSaveSignatureRef = useRef<string | null>(null);
  // One remote-version probe at a time; a focus burst must not fan out reads.
  const probeInFlightRef = useRef(false);
  // Which file owes the "Updated elsewhere" toast once its fresh scene is on
  // screen (`markHydrated`), so the toast never announces a reload that failed
  // — or fires on a different file the user switched to meanwhile.
  const pendingRemoteUpdateToastRef = useRef<string | null>(null);

  apiRef.current = excalidrawAPI;
  tokenRef.current = accessToken;
  onCreatedRef.current = onCreated;
  isCollaboratingRef.current = isCollaborating;
  onReloadFromCloudRef.current = onReloadFromCloud;

  // Set around teardown/unload flushes: see the pendingNew branch of onFlush.
  const suppressCreateRef = useRef(false);

  /** The create currently running, so `saveNow` can await the real outcome. */
  const createInFlightRef = useRef<Promise<boolean> | null>(null);

  const applyLifecycle = useCallback((state: FileLifecycleState) => {
    lifecycleRef.current = state;
    setLifecycle(state);
  }, []);

  const captureScene = useCallback(
    (
      fileId: string,
      elements: readonly ExcalidrawElement[],
      appState: Pick<AppState, "viewBackgroundColor">,
    ) => {
      const api = apiRef.current;
      if (!api) {
        return;
      }
      const liveAppState = api.getAppState();
      const previous = latestSceneRef.current;
      const signature = sceneSignature(elements, appState);
      latestSceneRef.current = {
        fileId,
        elements:
          previous?.fileId === fileId &&
          sceneSignature(previous.elements, previous.appState) === signature
            ? previous.elements
            : cloneJSON(elements),
        appState: {
          ...liveAppState,
          ...cloneJSON(clearAppStateForLocalStorage(liveAppState)),
          ...appState,
        },
        files: Object.fromEntries(
          Object.entries(api.getFiles()).map(([id, file]) => [id, { ...file }]),
        ),
      };
    },
    [],
  );

  const clearRetry = useCallback(() => {
    if (retryTimerRef.current !== null) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    backoffRef.current = INITIAL_BACKOFF_MS;
    failureCountRef.current = 0;
  }, []);

  // Both messages describe the file we were just editing, so every path that
  // stops driving a file drops them (a warning about another file's images, or a
  // dead error, is worse than silence).
  const clearSaveMessages = useCallback(() => {
    setImageWarning(null);
    setErrorMessage(null);
  }, []);

  /**
   * Report a failed save honestly: "offline" only when we could not reach a
   * server; a server that answered with an error is an error (see
   * data/saveFailure.ts). The work stays buffered either way.
   *
   * `message` is the actual reason (the server's message, or e.g. "Some images
   * failed to upload."). The chip's own wording is deliberately short, so
   * without this the one fact that tells the user WHY nothing is saving was
   * thrown away — it only ever reached a rejected promise nobody reads.
   */
  const reportFailure = useCallback(
    (kind: SaveFailureKind, message?: string | null) => {
      failureCountRef.current += 1;
      setErrorMessage(message ?? null);
      setStatus(
        classifySaveFailure({
          kind,
          isOnline: isBrowserOnline(),
          consecutiveFailures: failureCountRef.current,
        }),
      );
    },
    [],
  );

  // The scheduler is created once; its onFlush delegates through a ref so it can
  // reference the latest logic without being recreated (which would drop timers).
  const scheduler: AutosaveScheduler = useMemo(
    () => createAutosaveScheduler({ onFlush: () => onFlushRef.current!() }),
    [],
  );

  // Thumbnail generation hook point: the autosave success path below calls
  // `maybeGenerateThumbnail` after every successful save. The trigger module
  // is the only thing deciding "is this file due for a new thumbnail" (slow
  // debounce, ≤ once/30s); this hook additionally only fires it once the file
  // is "clean" (no further edit pending immediately after this save), so a
  // burst of saves during continuous editing doesn't export mid-flight.
  const thumbnailTrigger = useMemo(() => createThumbnailTrigger(), []);
  // One-shot timer for a *trailing* regeneration: when the LAST save of a
  // session is skipped only because of the cooldown, we still owe the file a
  // fresh thumbnail, so we arm a single regen for when the cooldown expires.
  // Cleared on unmount / file-switch / new-mindmap and whenever a fresh
  // generation supersedes it.
  const trailingThumbnailTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const clearTrailingThumbnail = useCallback(() => {
    if (trailingThumbnailTimerRef.current !== null) {
      clearTimeout(trailingThumbnailTimerRef.current);
      trailingThumbnailTimerRef.current = null;
    }
  }, []);

  // Fire generation (idle-priority, never awaited, never blocks the save that
  // just completed). Records the cooldown ONLY after a blob was genuinely
  // produced AND uploaded — a no-op/empty/failed export must not burn the 30s
  // window and suppress the next real thumbnail (`generateAndUploadThumbnail`
  // never rejects and reports success as its resolved value).
  const runThumbnailGeneration = useCallback(
    (
      fileId: string,
      nonDeleted: readonly ExcalidrawElement[],
      appState: AppState,
      files: BinaryFiles,
    ) => {
      const run = () =>
        void generateAndUploadThumbnail(
          fileId,
          nonDeleted,
          appState,
          files,
          tokenRef.current ?? "",
        ).then((generated) => {
          if (generated) {
            thumbnailTrigger.recordGenerated(fileId);
          }
        });
      if (
        typeof window !== "undefined" &&
        typeof window.requestIdleCallback === "function"
      ) {
        window.requestIdleCallback(run, { timeout: 5000 });
      } else {
        setTimeout(run, 0);
      }
    },
    [thumbnailTrigger],
  );

  // Arm the trailing regeneration for when the cooldown next allows one. At
  // fire time it re-reads the newest scene and re-checks the gate: still the
  // same open file, hydrated, no save pending/in-flight (so the last save
  // landed), and the cooldown has actually elapsed.
  const scheduleTrailingThumbnail = useCallback(
    (fileId: string) => {
      clearTrailingThumbnail();
      trailingThumbnailTimerRef.current = setTimeout(() => {
        trailingThumbnailTimerRef.current = null;
        const state = lifecycleRef.current;
        const api = apiRef.current;
        if (
          state.kind !== "open" ||
          state.fileId !== fileId ||
          !api ||
          !hydratedRef.current ||
          scheduler.isDirty() ||
          scheduler.isSaving() ||
          !thumbnailTrigger.shouldGenerate(fileId)
        ) {
          return;
        }
        const nonDeleted = getNonDeletedElements(
          api.getSceneElementsIncludingDeleted(),
        );
        const appState = api.getAppState();
        const files = api.getFiles();
        if (nonDeleted.length === 0) {
          // Emptied since the skipped save — remove the stale preview instead.
          void deleteThumbnail(fileId, tokenRef.current ?? "");
          return;
        }
        runThumbnailGeneration(fileId, nonDeleted, appState, files);
      }, thumbnailTrigger.msUntilReady(fileId));
    },
    [
      clearTrailingThumbnail,
      runThumbnailGeneration,
      scheduler,
      thumbnailTrigger,
    ],
  );

  const maybeGenerateThumbnail = useCallback(
    (
      fileId: string,
      elements: readonly ExcalidrawElement[],
      appState: AppState,
      files: BinaryFiles,
    ) => {
      // A further edit is already pending: the follow-up save will re-evaluate,
      // so don't export a scene that's about to change.
      if (scheduler.isDirty()) {
        return;
      }

      const nonDeleted = getNonDeletedElements(elements);

      // Emptied scene: there is nothing to depict, so remove any existing
      // thumbnail (best-effort) rather than leaving the old preview forever.
      if (nonDeleted.length === 0) {
        clearTrailingThumbnail();
        void deleteThumbnail(fileId, tokenRef.current ?? "");
        return;
      }

      if (thumbnailTrigger.shouldGenerate(fileId)) {
        // Fresh generation supersedes any trailing regen we were waiting on.
        clearTrailingThumbnail();
        runThumbnailGeneration(fileId, nonDeleted, appState, files);
        return;
      }

      // Skipped ONLY because of the cooldown — this may be the last save of the
      // session, so owe the file a trailing regen when the cooldown expires
      // (otherwise My Files shows the pre-cooldown preview forever).
      scheduleTrailingThumbnail(fileId);
    },
    [
      clearTrailingThumbnail,
      runThumbnailGeneration,
      scheduleTrailingThumbnail,
      scheduler,
      thumbnailTrigger,
    ],
  );

  const scheduleRetry = useCallback(() => {
    if (retryTimerRef.current !== null) {
      clearTimeout(retryTimerRef.current);
    }
    const delay = backoffRef.current;
    retryTimerRef.current = setTimeout(() => {
      // Cleared as it fires: a non-null handle means "a retry is still pending",
      // which `handleChange` reads to decide whether an edit may attempt a save
      // of its own. A spent handle left in place would block that forever.
      retryTimerRef.current = null;
      scheduler.flushNow();
    }, delay);
    backoffRef.current = Math.min(delay * 2, MAX_BACKOFF_MS);
  }, [scheduler]);

  // Returns false on a storage failure or when no snapshot belongs to this file.
  const bufferScene = useCallback((fileId: string): boolean => {
    const snapshot = latestSceneRef.current;
    if (!snapshot || snapshot.fileId !== fileId) {
      return false;
    }
    try {
      return saveCloudFileBuffer(fileId, {
        elements: clearElementsForLocalStorage(snapshot.elements),
        appState: clearAppStateForLocalStorage(snapshot.appState),
        // Stamped so a replay in a LATER session declares the version this
        // scene was actually built on, not whatever is current by then.
        baseVersion: lastVersionRef.current,
      });
    } catch (error) {
      console.error(error);
      return false;
    }
  }, []);

  // Signature of the scene currently on screen, or null when it can't be read
  // (no editor yet, or a torn-down API on the unmount path).
  const currentSceneSignature = useCallback((): string | null => {
    const api = apiRef.current;
    if (!api) {
      return null;
    }
    try {
      return sceneSignature(
        api.getSceneElementsIncludingDeleted(),
        api.getAppState(),
      );
    } catch (error) {
      console.error(error);
      return null;
    }
  }, []);

  // Snapshot the on-screen scene before the file is torn down. Deliberately
  // wider than `isDirty()`: a flush resets `dirty` as it starts and `flushNow()`
  // no-ops mid-save, so an edit made during an in-flight save exists nowhere
  // else. Bumps the buffer generation so that save's success handler knows a
  // newer scene owns the buffer and must not clear it.
  //
  // But ONLY when the scene really is newer. A teardown during an in-flight save
  // with no new edit used to buffer a scene identical to that save's payload and
  // bump anyway, which permanently vetoed the save's `clearCloudFileBuffer` —
  // leaving an orphan buffer that App.tsx restores un-synced on next open,
  // costing a spurious PUT + version bump. That scene is already in the
  // write-through buffer the save itself wrote, so skipping both is lossless.
  const bufferCurrentSceneForTeardown = useCallback((): {
    dirtyOrSaving: boolean;
    bufferOk: boolean;
  } => {
    const state = lifecycleRef.current;
    const dirtyOrSaving = scheduler.isDirty() || scheduler.isSaving();
    let bufferOk = true;
    if (state.kind === "open" && hydratedRef.current && dirtyOrSaving) {
      const inFlightSignature = inFlightSaveSignatureRef.current;
      const isInFlightSaveScene =
        inFlightSignature !== null &&
        latestSceneRef.current?.fileId === state.fileId &&
        sceneSignature(
          latestSceneRef.current.elements,
          latestSceneRef.current.appState,
        ) === inFlightSignature;
      if (!isInFlightSaveScene) {
        bufferOk = bufferScene(state.fileId);
        bufferGenerationRef.current += 1;
      }
    }
    return { dirtyOrSaving, bufferOk };
  }, [bufferScene, scheduler]);

  // Shared teardown for every path that stops driving the current file
  // (file-switch, back-to-scratch, new-mindmap, unmount): snapshot, flush, cancel.
  const teardownCurrentFile = useCallback(() => {
    bufferCurrentSceneForTeardown();
    suppressCreateRef.current = true;
    scheduler.flushNow();
    suppressCreateRef.current = false;
    scheduler.cancel();
  }, [bufferCurrentSceneForTeardown, scheduler]);

  /**
   * Stop driving the file and hand it back to the loader (both conflict exits).
   * The buffer goes too: it holds exactly the scene being discarded, and would
   * resurrect it on next open.
   */
  const reloadFromCloud = useCallback(
    (fileId: string, current: number, options?: { silent?: boolean }) => {
      scheduler.cancel();
      clearCloudFileBuffer(fileId);
      lastVersionRef.current = current;
      syncedSignatureRef.current = null;
      // The editor no longer holds this file's scene: no save may run until the
      // loader has put the fresh one there and called `markHydrated`.
      hydratedRef.current = false;
      setHasUnsavedChanges(false);
      setStatus("idle");
      onReloadFromCloudRef.current?.(options);
    },
    [scheduler],
  );

  /**
   * The server holds a newer version than this tab built on — from a refused
   * save (`"save"`) or the version probe (`"probe"`). Reload without asking
   * only when the scene on screen is byte-identical to the last commit; any
   * other content is the user's work, so the banner asks.
   */
  const handleNewerRemoteVersion = useCallback(
    (fileId: string, current: number, source: "save" | "probe") => {
      const isClean =
        syncedSignatureRef.current !== null &&
        currentSceneSignature() === syncedSignatureRef.current;
      if (isClean) {
        if (source === "probe") {
          pendingRemoteUpdateToastRef.current = fileId;
          reloadFromCloud(fileId, current, { silent: true });
          return;
        }
        reloadFromCloud(fileId, current);
        return;
      }
      scheduler.cancel();
      clearRetry();
      conflictRef.current = { current };
      setConflict({ current });
      setHasUnsavedChanges(true);
      setStatus("idle");
      setErrorMessage(null);
    },
    [clearRetry, currentSceneSignature, reloadFromCloud, scheduler],
  );

  /**
   * Ask the server for the file's current version and react if it moved on.
   * Skipped when the answer could not be acted on safely (no hydrated open
   * file, save in flight, banner up, live collab room, probe already out);
   * the guards are re-checked after the read, since any can flip meanwhile.
   */
  const probeRemoteVersion = useCallback(async () => {
    const state = lifecycleRef.current;
    const canProbe = () =>
      lifecycleRef.current === state &&
      state.kind === "open" &&
      hydratedRef.current &&
      !conflictRef.current &&
      !scheduler.isSaving() &&
      !isCollaboratingRef.current?.() &&
      lastVersionRef.current !== null;
    if (probeInFlightRef.current || !canProbe() || state.kind !== "open") {
      return;
    }
    probeInFlightRef.current = true;
    let remote: number | null = null;
    try {
      remote = (await getFile(state.fileId))?.version ?? null;
    } catch (error) {
      // Best effort: a failed read is just "no news"; the next probe retries.
      // Offline, every probe fails the same way — not worth a log line each.
      if (isBrowserOnline()) {
        console.error(error);
      }
      return;
    } finally {
      probeInFlightRef.current = false;
    }
    if (remote === null || !canProbe() || remote <= lastVersionRef.current!) {
      return;
    }
    handleNewerRemoteVersion(state.fileId, remote, "probe");
  }, [handleNewerRemoteVersion, scheduler]);

  // The actual save for an open file. Buffers first (write-through), attempts
  // the PUT, then either clears the buffer + records the version, or keeps the
  // buffer and schedules a backoff retry (offline).
  onFlushRef.current = async () => {
    const state = lifecycleRef.current;
    const api = apiRef.current;
    // Paused pending the user's answer — see `handleNewerRemoteVersion`.
    if (conflictRef.current) {
      return;
    }
    // No row yet — the save this flush is asking for IS the creation. Without
    // this, every flush in `pendingNew` returned silently: a failed create only
    // ever retried on the next edit (the backoff timer calls `flushNow`), and
    // "Save now" / Ctrl+S were dead buttons in exactly the state whose chip says
    // the work is unsaved.
    if (
      state.kind === "pendingNew" &&
      api &&
      hasContent(api.getSceneElements())
    ) {
      // A teardown/unload flush must not mint a row: the request cannot finish
      // during unload, and the scene is already in the scratch keys (see
      // `isPreCreate`). Creating here left orphan "Untitled" rows behind.
      if (suppressCreateRef.current) {
        return;
      }
      if (!(await beginCreate())) {
        // Reject exactly like the open-file path: the scheduler re-runs a
        // resolved flush while dirty, which would be a tight retry loop. The
        // backoff timer armed by the failure owns the retry.
        throw new Error("Could not create the file.");
      }
      return;
    }
    if (state.kind !== "open" || !api) {
      return;
    }
    // Never write the pre-hydration scene over the file (e.g. a beforeunload
    // flush fired while the file was still loading).
    if (
      !isSafeToSaveCloudScene({
        lifecycleKind: state.kind,
        isHydrated: hydratedRef.current,
      })
    ) {
      return;
    }
    const fileId = state.fileId;
    const snapshot = latestSceneRef.current;
    if (!snapshot || snapshot.fileId !== fileId) {
      return;
    }
    const { elements, appState, files } = snapshot;

    // Write-through buffer + local image persistence (never scratch keys).
    bufferScene(fileId);
    const bufferGenerationAtSave = bufferGenerationRef.current;
    // Let a teardown recognise "this is the very scene being saved" (Fix 3).
    inFlightSaveSignatureRef.current = sceneSignature(elements, appState);
    // The blobs still go to IndexedDB, but during a live session the element
    // stamp is the room's protocol signal — a local write must not claim
    // "saved" while the room upload is still in flight (mirrors LocalData's
    // pauseSave("collaboration"), which neuters the scratch path's equivalent).
    void LocalData.fileStorage
      .saveFiles({ elements, files })
      .then(() => {
        if (!isCollaboratingRef.current?.()) {
          markImagesSaved(api);
        }
      })
      .catch((error) => console.error(error));

    setStatus("saving");
    const {
      version,
      errorMessage: saveError,
      errorKind,
      assetWarning,
      stale,
    } = await saveSceneToCloud(
      fileId,
      elements,
      appState,
      files,
      tokenRef.current ?? "",
      lastVersionRef.current,
    );
    // No longer in flight — later teardowns have nothing to compare against.
    inFlightSaveSignatureRef.current = null;

    // We may have switched files while this save was in flight (a file-switch
    // flushes the outgoing file, fire-and-forget). The PUT above still persists
    // the correct file, but the shared status/version/signature refs now belong
    // to the newly-open file — don't clobber them with this file's result.
    // Refused as stale: another writer got there first. Never a retry — the
    // same bytes on the same base version can only be refused again — so this
    // path deliberately does NOT mark dirty or arm the backoff.
    if (stale) {
      if (
        lifecycleRef.current.kind === "open" &&
        lifecycleRef.current.fileId === fileId
      ) {
        handleNewerRemoteVersion(fileId, stale.current, "save");
      }
      return;
    }

    const settled = lifecycleRef.current;
    if (
      !mountedRef.current ||
      settled.kind !== "open" ||
      settled.fileId !== fileId
    ) {
      if (saveError || version === null) {
        // Keep it buffered (already is) but reject quietly; no retry is armed
        // for a file we've navigated away from.
        throw new Error(saveError ?? "Cloud save failed.");
      }
      // Only clear the buffer if it still holds the scene we just persisted —
      // a teardown may have buffered a newer mid-flight edit.
      if (bufferGenerationRef.current === bufferGenerationAtSave) {
        clearCloudFileBuffer(fileId);
        if (!mountedRef.current) {
          const nonDeleted = getNonDeletedElements(elements);
          if (nonDeleted.length === 0) {
            void deleteThumbnail(fileId, tokenRef.current ?? "");
          } else {
            runThumbnailGeneration(fileId, nonDeleted, appState, files);
          }
        }
      }
      return;
    }

    if (saveError || version === null) {
      // Offline / server error: work stays in the buffer. Keep it dirty so the
      // backoff retry's `flushNow` has something to flush, arm the backoff
      // timer, then reject: the scheduler must NOT immediately re-run this
      // (that would be a tight loop) — retries come only from the backoff timer
      // (or a reconnect / fresh edit).
      scheduler.markDirty();
      scheduleRetry();
      // A save that never landed leaves the file dirty, whatever the reason.
      setHasUnsavedChanges(true);
      reportFailure(errorKind ?? "server", saveError);
      throw new Error(saveError ?? "Cloud save failed.");
    }

    clearCloudFileBuffer(fileId);
    clearRetry();
    setErrorMessage(null);
    // Fresh from this save: either it warns about skipped images, or it clears
    // the previous warning.
    setImageWarning(assetWarning ?? null);
    syncedSignatureRef.current = sceneSignature(elements, appState);
    if (isVersionConflict(lastVersionRef.current, version)) {
      setHasConflict(true);
    }
    lastVersionRef.current = version;
    setStatus("saved");
    setSavedAt(Date.now());
    // Only clean if no further edit arrived while this save was in flight.
    setHasUnsavedChanges(scheduler.isDirty());
    maybeGenerateThumbnail(fileId, elements, appState, files);
    // Any confirmed save is a moment worth asking on, autosave flushes
    // included: someone who keeps editing one existing file never creates
    // another and never opens the manual dialog, so without this nothing would
    // ever ask them again. The nudge's own cooldown decides whether a given
    // trigger is acted on, so a hot autosave loop costs one localStorage read.
    fireNudgeTrigger();
  };

  // Create-on-first-edit: materialize the row (title "Untitled") + first PUT,
  // then hand the id to the parent to switch the URL to /f/<id>.
  const createOnFirstEdit = useCallback(async (): Promise<boolean> => {
    const api = apiRef.current;
    if (!api) {
      return false;
    }
    // Reuse the row from a previous failed attempt if one exists, so a retry
    // does not orphan an "Untitled" row. `rowCreated` tracks whether the DB row
    // exists yet, so the failure path only retains the id once it truly does.
    const current = lifecycleRef.current;
    const existingId = current.kind === "creating" ? current.fileId : undefined;
    const id = existingId ?? crypto.randomUUID();
    let rowCreated = existingId !== undefined;
    try {
      const elements = api.getSceneElementsIncludingDeleted();
      if (!rowCreated) {
        await createFile({ id, title: "Untitled" });
        rowCreated = true;
      }
      const {
        version,
        errorMessage: saveError,
        errorKind,
        assetWarning,
      } = await saveSceneToCloud(
        id,
        elements,
        api.getAppState(),
        api.getFiles(),
        tokenRef.current ?? "",
        // The row was just inserted, so its version is the table default —
        // nobody can have committed on top of a file that did not exist.
        CLOUD_FILE_INITIAL_VERSION,
      );
      if (saveError || version === null) {
        // Carry the classification through the throw so the catch below can
        // report offline-vs-error honestly.
        throw Object.assign(
          new Error(saveError ?? "Could not create the file."),
          { errorKind: errorKind ?? "server" },
        );
      }
      lastVersionRef.current = version;
      syncedSignatureRef.current = sceneSignature(elements, api.getAppState());
      // The row exists and holds the scene — but if the user has since started
      // a new mindmap or opened another file, adopting it now would drag the
      // editor back onto the file they walked away from. It is in My Files
      // either way.
      if (!mountedRef.current || lifecycleRef.current.kind !== "creating") {
        return true;
      }
      captureScene(
        id,
        api.getSceneElementsIncludingDeleted(),
        api.getAppState(),
      );
      const { state } = fileLifecycleReducer(lifecycleRef.current, {
        type: "CREATED",
        fileId: id,
      });
      applyLifecycle(state);
      clearRetry();
      setErrorMessage(null);
      setImageWarning(assetWarning ?? null);
      setStatus("saved");
      setSavedAt(Date.now());
      setHasUnsavedChanges(false);
      maybeGenerateThumbnail(id, elements, api.getAppState(), api.getFiles());
      onCreatedRef.current(id);
      fireNudgeTrigger();
      // Capture any edits made while the row was being created.
      scheduler.notifyChange();
    } catch (error) {
      console.error(error);
      // Retain the id only if the row actually exists, so the next edit retries
      // the save on the same row rather than minting a new orphan.
      const { state } = fileLifecycleReducer(lifecycleRef.current, {
        type: "CREATE_FAILED",
        fileId: rowCreated ? id : undefined,
      });
      applyLifecycle(state);
      setHasUnsavedChanges(true);
      // Same re-arming the open-file save does: keep the work dirty and put a
      // backoff retry behind it. Without this a failed create waited for the
      // user's next stroke — every other retry path (Save now, Ctrl+S, the
      // reconnect handler, the backoff timer) goes through `flushNow`, which the
      // scheduler drops unless something is dirty.
      scheduler.markDirty();
      scheduleRetry();
      // `createFile` (Supabase) rejects with a TypeError only when the request
      // never reached the server; anything else answered us.
      const kind: SaveFailureKind =
        (error as { errorKind?: SaveFailureKind })?.errorKind ??
        (error instanceof TypeError ? "network" : "server");
      reportFailure(kind, (error as Error)?.message ?? null);
      return false;
    }
    return true;
  }, [
    applyLifecycle,
    captureScene,
    clearRetry,
    maybeGenerateThumbnail,
    reportFailure,
    scheduleRetry,
    scheduler,
  ]);

  /**
   * Start (or retry) create-on-first-edit THROUGH the reducer, so the lifecycle
   * reads `creating` for the duration. That transition is the guard everything
   * else uses: `createOnFirstEdit` reuses the row from a `creating` state rather
   * than minting a fresh uuid, so a flush that skipped it raced any concurrent
   * edit into two "Untitled" rows and two `onCreated` callbacks.
   */
  const beginCreate = useCallback(async (): Promise<boolean> => {
    const { state, effect } = fileLifecycleReducer(lifecycleRef.current, {
      type: "EDIT",
    });
    if (effect !== "createFile") {
      // Already creating, or not a pre-create state at all.
      return false;
    }
    applyLifecycle(state);
    const running = createOnFirstEdit();
    createInFlightRef.current = running;
    try {
      return await running;
    } finally {
      if (createInFlightRef.current === running) {
        createInFlightRef.current = null;
      }
    }
  }, [applyLifecycle, createOnFirstEdit]);

  const handleChange = useCallback(
    (
      elements: readonly ExcalidrawElement[],
      appState: Pick<AppState, "viewBackgroundColor">,
    ) => {
      const state = lifecycleRef.current;

      // The file is open but its scene hasn't reached the editor yet: this
      // change is the PREVIOUS scene talking (the mount echo of the scratch
      // canvas). Saving it would overwrite the file with a scene that was never
      // in it — the empty-canvas-over-your-work bug. Ignore until hydrated.
      if (
        !isSafeToSaveCloudScene({
          lifecycleKind: state.kind,
          isHydrated: hydratedRef.current,
        })
      ) {
        return;
      }

      // A fresh "New mindmap" stays a no-op (no DB row) until real content
      // appears — clearing the canvas to empty must not create a file.
      if (state.kind === "pendingNew" && !hasContent(elements)) {
        return;
      }

      if (state.kind === "open") {
        captureScene(state.fileId, elements, appState);
      }

      // Ignore echoes with no content change (notably the onChange fired by
      // hydrating the scene on open), which would otherwise churn the version.
      //
      // The in-flight signature counts too: an echo of the scene mid-PUT would
      // otherwise queue an identical second save ("Keep mine" wrote the same
      // bytes twice, one version apart).
      if (state.kind === "open") {
        const signature = sceneSignature(elements, appState);
        if (
          signature === syncedSignatureRef.current ||
          signature === inFlightSaveSignatureRef.current
        ) {
          return;
        }
      }

      const { state: nextState, effect } = fileLifecycleReducer(state, {
        type: "EDIT",
      });

      // A create already failed and its backoff retry is pending: let the timer
      // own the retry. Attempting another now feeds itself (failure → state →
      // re-render → `onChange` → attempt; measured at ~63/s). Must precede
      // `applyLifecycle`, which would move to `creating` with no create running
      // and make every later flush a no-op.
      if (effect === "createFile" && retryTimerRef.current !== null) {
        setHasUnsavedChanges(true);
        scheduler.markDirty();
        return;
      }

      applyLifecycle(nextState);

      if (effect === "createFile" && isCollaboratingRef.current?.()) {
        // A live room owns this canvas. Creating here rewrote the URL to
        // /files/<id> and dropped the #room= hash, so copying the link shared a
        // private file, reloading left the room, and every peer's edits
        // autosaved into the initiator's "Untitled". The scene stays in the
        // scratch keys, exactly as it did before this flow existed.
        return;
      }
      // A conflict is open: the edit is real and unsaved, but scheduling a save
      // for it would only earn another refusal.
      if (conflictRef.current) {
        setHasUnsavedChanges(true);
        return;
      }
      if (effect === "createFile") {
        setHasUnsavedChanges(true);
        // Dirty WITHOUT arming the debounce (the create is already starting).
        // This is what makes the work visible to everything that asks "is
        // anything unsaved": the beforeunload confirm, and `flushNow` — which
        // the Save-now button, Ctrl+S and the failure backoff all go through,
        // and which the scheduler drops on the floor unless something is dirty.
        scheduler.markDirty();
        void createOnFirstEdit();
      } else if (effect === "autosave") {
        setHasUnsavedChanges(true);
        scheduler.notifyChange();
      }
    },
    [applyLifecycle, captureScene, createOnFirstEdit, scheduler],
  );

  const markSynced = useCallback(
    (
      elements: readonly ExcalidrawElement[],
      appState: Pick<AppState, "viewBackgroundColor">,
      version?: number | null,
    ) => {
      syncedSignatureRef.current = sceneSignature(elements, appState);
      if (version != null) {
        lastVersionRef.current = version;
      }
    },
    [],
  );

  const markHydrated = useCallback(() => {
    hydratedRef.current = true;
    const hydratedState = lifecycleRef.current;
    if (
      hydratedState.kind === "open" &&
      pendingRemoteUpdateToastRef.current === hydratedState.fileId
    ) {
      // The agent's scene is on screen now — say so.
      pendingRemoteUpdateToastRef.current = null;
      apiRef.current?.setToast({ message: REMOTE_UPDATE_TOAST });
    }
    // A buffer for this file means the scene now in the editor may be the
    // buffered one, which was built on the version stamped into it rather than
    // on whatever the loader just read. Saving it against the newer version
    // would overwrite every commit made since — so the stamped version wins,
    // and the server gets to refuse the replay.
    const state = lifecycleRef.current;
    if (state.kind === "open") {
      const buffered = loadCloudFileBuffer(state.fileId);
      if (buffered?.baseVersion != null) {
        lastVersionRef.current = buffered.baseVersion;
      }
    }
  }, []);

  const resolveConflict = useCallback(
    (choice: "reload" | "keep") => {
      const pending = conflictRef.current;
      const state = lifecycleRef.current;
      if (!pending || state.kind !== "open") {
        return;
      }
      conflictRef.current = null;
      setConflict(null);

      if (choice === "reload") {
        reloadFromCloud(state.fileId, pending.current);
        return;
      }

      // Keep mine: commit on top of the version that won (the 409 carried it,
      // so no re-read is needed).
      lastVersionRef.current = pending.current;
      clearRetry();
      scheduler.markDirty();
      setHasUnsavedChanges(true);
      scheduler.flushNow();
    },
    [clearRetry, reloadFromCloud, scheduler],
  );

  const startNewMindmap = useCallback(
    (options?: { discardPending?: boolean }) => {
      const current = lifecycleRef.current;
      if (options?.discardPending) {
        // The user was shown what was unsaved and chose to throw it away: drop
        // the pending save AND the offline buffer, so the file does not
        // resurrect the discarded scene the next time it is opened.
        scheduler.cancel();
        const pendingFileId = "fileId" in current ? current.fileId : undefined;
        if (pendingFileId) {
          clearCloudFileBuffer(pendingFileId);
        }
      } else {
        teardownCurrentFile();
      }
      clearRetry();
      clearTrailingThumbnail();
      lastVersionRef.current = null;
      syncedSignatureRef.current = null;
      latestSceneRef.current = null;
      // Nothing left to announce about the file we just left.
      pendingRemoteUpdateToastRef.current = null;
      // A new mindmap has nothing to load: the blank editor IS the scene.
      hydratedRef.current = true;
      setHasConflict(false);
      conflictRef.current = null;
      setConflict(null);
      setStatus("idle");
      setSavedAt(null);
      setHasUnsavedChanges(false);
      clearSaveMessages();
      const { state } = fileLifecycleReducer(current, {
        type: "NEW_MINDMAP",
      });
      applyLifecycle(state);
    },
    [
      applyLifecycle,
      clearRetry,
      clearSaveMessages,
      clearTrailingThumbnail,
      scheduler,
      teardownCurrentFile,
    ],
  );

  // beforeunload: same snapshot as the teardown paths but WITHOUT cancelling —
  // the tab may survive, and an idle save should still fire its PUT. Returns
  // whether unsaved work should force the unload confirm.
  /**
   * The awaitable save. In a pre-create state it runs the creation and reports
   * whether it landed; for an open file it flushes and reports whether anything
   * is still outstanding afterwards.
   */
  const saveNow = useCallback(async (): Promise<boolean> => {
    const state = lifecycleRef.current;
    const api = apiRef.current;
    if (state.kind === "pendingNew") {
      return api && hasContent(api.getSceneElements())
        ? await beginCreate()
        : true;
    }
    // A create is already running: wait for THAT, rather than answering for it.
    // Saying "saved" here let "Save it first" blank the canvas mid-create, and
    // the create then adopted the fresh canvas onto the file it had just made.
    if (state.kind === "creating") {
      return createInFlightRef.current
        ? await createInFlightRef.current
        : false;
    }
    scheduler.flushNow();
    return !scheduler.isDirty();
  }, [beginCreate, scheduler]);

  const flushBeforeUnload = useCallback((): boolean => {
    const { dirtyOrSaving, bufferOk } = bufferCurrentSceneForTeardown();
    suppressCreateRef.current = true;
    scheduler.flushNow();
    suppressCreateRef.current = false;
    // A failed buffer write (quota) means the edit is nowhere safe — force the
    // unload confirm so the user isn't silently losing work.
    return dirtyOrSaving || !bufferOk;
  }, [bufferCurrentSceneForTeardown, scheduler]);

  // Adopt a file opened directly via /f/<id> (or after create-on-first-edit
  // navigation). Guarded so we don't clobber a version we just recorded.
  useEffect(() => {
    const current = lifecycleRef.current;
    if (!fileIdFromPath) {
      // Navigated back to the scratch canvas (browser Back/Forward). Only an
      // OPEN file needs unwinding — pendingNew/creating at `/` is a deliberate
      // "New mindmap" (create-on-first-edit) and must stay put. Without this,
      // the lifecycle stayed "open" and autosave kept PUTting the on-screen
      // scene to the file we'd navigated away from.
      if (current.kind === "open") {
        // Tear down FIRST so no later write targets the file once we're back
        // on scratch.
        teardownCurrentFile();
        clearRetry();
        clearTrailingThumbnail();
        lastVersionRef.current = null;
        syncedSignatureRef.current = null;
        hydratedRef.current = true;
        setHasConflict(false);
        conflictRef.current = null;
        setConflict(null);
        setStatus("idle");
        setSavedAt(null);
        setHasUnsavedChanges(false);
        clearSaveMessages();
        applyLifecycle({ kind: "scratch" });
      }
      return;
    }
    if (current.kind === "open" && current.fileId === fileIdFromPath) {
      return;
    }
    // Tear down the file we're leaving; the write-through buffer preserves the
    // payload, so this is fire-and-forget.
    teardownCurrentFile();
    clearRetry();
    clearTrailingThumbnail();
    setHasConflict(false);
    conflictRef.current = null;
    setConflict(null);
    setStatus("idle");
    setSavedAt(null);
    setHasUnsavedChanges(false);
    clearSaveMessages();
    lastVersionRef.current = loadedVersion ?? null;
    syncedSignatureRef.current = null;
    // A toast owed by the file we are leaving must not greet its next open.
    pendingRemoteUpdateToastRef.current = null;
    // Adopting a file from the URL: its scene is NOT in the editor yet. No save
    // may happen until the loader calls `markHydrated` — otherwise the scene
    // currently on screen (the previous file / the scratch canvas) would be
    // written over this file.
    hydratedRef.current = false;
    const { state } = fileLifecycleReducer(current, {
      type: "OPEN_EXISTING",
      fileId: fileIdFromPath,
    });
    applyLifecycle(state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileIdFromPath]);

  // Seed the conflict baseline from the loaded metadata once it resolves.
  useEffect(() => {
    if (loadedVersion != null && lastVersionRef.current == null) {
      lastVersionRef.current = loadedVersion;
    }
  }, [loadedVersion]);

  // Remote-version probe: on focus / becoming visible, and on the slow interval
  // while visible. A hidden tab only wastes reads (nobody is looking), and it
  // probes the moment it is looked at again.
  useEffect(() => {
    const isVisible = () => document.visibilityState !== "hidden";
    const onFocus = () => void probeRemoteVersion();
    const onVisibilityChange = () => {
      if (isVisible()) {
        void probeRemoteVersion();
      }
    };
    const interval = setInterval(() => {
      if (isVisible()) {
        void probeRemoteVersion();
      }
    }, REMOTE_VERSION_PROBE_INTERVAL_MS);
    window.addEventListener(EVENT.FOCUS, onFocus);
    document.addEventListener(EVENT.VISIBILITY_CHANGE, onVisibilityChange);
    return () => {
      clearInterval(interval);
      window.removeEventListener(EVENT.FOCUS, onFocus);
      document.removeEventListener(EVENT.VISIBILITY_CHANGE, onVisibilityChange);
    };
  }, [probeRemoteVersion]);

  // Flush buffered work as soon as connectivity returns.
  useEffect(() => {
    const onOnline = () => {
      clearRetry();
      scheduler.flushNow();
    };
    window.addEventListener(EVENT.ONLINE, onOnline);
    return () => window.removeEventListener(EVENT.ONLINE, onOnline);
  }, [clearRetry, scheduler]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      teardownCurrentFile();
      clearRetry();
      clearTrailingThumbnail();
    };
  }, [teardownCurrentFile, clearRetry, clearTrailingThumbnail]);

  return {
    isCloudContext: lifecycle.kind !== "scratch",
    // No usable cloud copy yet, so App.tsx keeps writing the scratch
    // localStorage keys while this is true — see the note there. `creating`
    // counts: the row is not confirmed and edits made during the PUT are in the
    // same position as the ones before it.
    isPreCreate:
      lifecycle.kind === "pendingNew" || lifecycle.kind === "creating",
    status,
    savedAt,
    hasConflict,
    conflict,
    resolveConflict,
    imageWarning,
    errorMessage,
    hasUnsavedChanges,
    handleChange,
    markSynced,
    markHydrated,
    startNewMindmap,
    flushNow: scheduler.flushNow,
    saveNow,
    flushBeforeUnload,
  };
};
